// ═══════════════════════════════════════════════════════════════════════════════
// Modal composer for comments (avoids inline textarea keyboard issues on iOS PWA)
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';
import { AppButton } from '../ui/AppButton';
import { AppModal } from '../ui/AppModal';

type CommentComposerModalProps = {
  open: boolean;
  title: string;
  placeholder: string;
  draft: string;
  maxCommentLength: number;
  sending: boolean;
  sendError: string | null;
  canSend: boolean;
  charCountLabel: string;
  shortcutHint: string;
  sendLabel: string;
  sendingLabel: string;
  closeLabel: string;
  sendAriaLabel: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onClose: () => void;
};

export const CommentComposerModal = ({
  open,
  title,
  placeholder,
  draft,
  maxCommentLength,
  sending,
  sendError,
  canSend,
  charCountLabel,
  shortcutHint,
  sendLabel,
  sendingLabel,
  closeLabel,
  sendAriaLabel,
  onDraftChange,
  onSend,
  onClose,
}: CommentComposerModalProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const focusTimer = window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(focusTimer);
  }, [open]);

  const handleComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (canSend) {
        onSend();
      }
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={title}
      titleId="comment-composer-modal-title"
      closeAriaLabel={closeLabel}
      closeDisabled={sending}
    >
      <div className="space-y-2">
        <label htmlFor="comment-composer-modal-draft" className="sr-only">
          {placeholder}
        </label>
        <textarea
          ref={textareaRef}
          id="comment-composer-modal-draft"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={handleComposerKeyDown}
          maxLength={4000}
          rows={5}
          enterKeyHint="enter"
          disabled={sending}
          placeholder={placeholder}
          className="min-h-[7rem] w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:ring-primary-800 dark:disabled:bg-gray-800/80"
          aria-label={placeholder}
          aria-describedby="comment-composer-char-hint comment-composer-shortcut-hint"
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p
            id="comment-composer-char-hint"
            className={`text-xs ${draft.length > maxCommentLength ? 'font-medium text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}
            aria-live="polite"
          >
            {charCountLabel}
          </p>
          <span id="comment-composer-shortcut-hint" className="sr-only">
            {shortcutHint}
          </span>
        </div>
        {sendError ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {sendError}
          </p>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <AppButton
            type="button"
            variant="primary"
            onClick={onSend}
            disabled={!canSend}
            aria-label={sendAriaLabel}
          >
            {sending ? sendingLabel : sendLabel}
          </AppButton>
        </div>
      </div>
    </AppModal>
  );
};
